import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { 
  Globe, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  Copy, 
  RefreshCw,
  Info,
  ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomDomainSection() {
  const [domain, setDomain] = useState('');
  const [currentDomain, setCurrentDomain] = useState(null);
  const [verified, setVerified] = useState(false);
  const [verificationRecord, setVerificationRecord] = useState(null);
  const [verificationValue, setVerificationValue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    loadDomainSettings();
  }, []);

  const loadDomainSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/store/domain`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      setCurrentDomain(data.custom_domain);
      setDomain(data.custom_domain || '');
      setVerified(data.custom_domain_verified);
      setVerificationRecord(data.verification_record);
      setVerificationValue(data.verification_value);
    } catch (error) {
      console.error('Errore caricamento impostazioni dominio:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDomain = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/store/domain`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ custom_domain: domain || null })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Errore salvataggio');
      }
      
      setCurrentDomain(data.custom_domain);
      setVerificationRecord(data.verification_record);
      setVerificationValue(data.verification_value);
      setVerified(false);
      
      toast.success('Dominio aggiornato');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/store/domain/verify`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (data.verified) {
        setVerified(true);
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Errore durante la verifica');
    } finally {
      setVerifying(false);
    }
  };

  const handleRemoveDomain = async () => {
    if (!window.confirm('Rimuovere il dominio personalizzato?')) return;
    
    setDomain('');
    await handleSaveDomain();
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
    toast.success('Copiato!');
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Dominio Personalizzato
          </CardTitle>
          <CardDescription>
            Configura un dominio personalizzato per il tuo negozio white-label
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Current Status */}
          {currentDomain && (
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium">{currentDomain}</p>
                  <p className="text-sm text-muted-foreground">Dominio attuale</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {verified ? (
                  <Badge className="bg-green-500">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Verificato
                  </Badge>
                ) : (
                  <Badge variant="destructive">
                    <XCircle className="w-3 h-3 mr-1" />
                    Non verificato
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Domain Input */}
          <div className="space-y-2">
            <Label>Dominio</Label>
            <div className="flex gap-2">
              <Input
                value={domain}
                onChange={(e) => setDomain(e.target.value.toLowerCase())}
                placeholder="mionegozio.com"
                data-testid="domain-input"
              />
              <Button 
                onClick={handleSaveDomain} 
                disabled={saving || domain === currentDomain}
                data-testid="save-domain-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salva'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Inserisci il dominio senza http:// o www. (es: mionegozio.com)
            </p>
          </div>

          {/* Verification Instructions */}
          {currentDomain && !verified && (
            <Alert>
              <Info className="w-4 h-4" />
              <AlertDescription>
                <div className="space-y-4">
                  <p className="font-medium">Istruzioni per la verifica:</p>
                  <ol className="list-decimal list-inside space-y-2 text-sm">
                    <li>Accedi al pannello DNS del tuo provider di dominio</li>
                    <li>Aggiungi un nuovo record TXT con i seguenti valori:</li>
                  </ol>
                  
                  <div className="grid gap-3 mt-4">
                    <div className="bg-muted p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Nome Record (Host)</p>
                      <div className="flex items-center justify-between">
                        <code className="text-sm font-mono">{verificationRecord}</code>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => copyToClipboard(verificationRecord, 'record')}
                        >
                          {copied === 'record' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    
                    <div className="bg-muted p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Valore</p>
                      <div className="flex items-center justify-between">
                        <code className="text-sm font-mono">{verificationValue}</code>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => copyToClipboard(verificationValue, 'value')}
                        >
                          {copied === 'value' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <ol className="list-decimal list-inside space-y-2 text-sm" start="3">
                    <li>Attendi la propagazione DNS (può richiedere fino a 24 ore)</li>
                    <li>Clicca "Verifica dominio" per completare la configurazione</li>
                  </ol>
                  
                  <Button 
                    onClick={handleVerify} 
                    disabled={verifying}
                    className="w-full mt-4"
                    data-testid="verify-domain-btn"
                  >
                    {verifying ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Verifica in corso...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Verifica Dominio
                      </>
                    )}
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Verified Success */}
          {currentDomain && verified && (
            <Alert className="border-green-500 bg-green-50">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <AlertDescription className="text-green-800">
                <p className="font-medium">Dominio verificato!</p>
                <p className="text-sm mt-1">
                  Il tuo negozio è accessibile su{' '}
                  <a 
                    href={`https://${currentDomain}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="underline inline-flex items-center gap-1"
                  >
                    {currentDomain}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3"
                  onClick={handleRemoveDomain}
                >
                  Rimuovi dominio
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* DNS Provider Links */}
          <div className="pt-4 border-t">
            <p className="text-sm text-muted-foreground mb-3">Guide configurazione DNS:</p>
            <div className="flex flex-wrap gap-2">
              {[
                { name: 'Cloudflare', url: 'https://developers.cloudflare.com/dns/manage-dns-records/how-to/create-dns-records/' },
                { name: 'GoDaddy', url: 'https://www.godaddy.com/help/add-a-txt-record-19232' },
                { name: 'Aruba', url: 'https://guide.hosting.aruba.it/domini/gestione-dns.aspx' },
                { name: 'Register.it', url: 'https://www.register.it/assistenza/dns/' }
              ].map(provider => (
                <a
                  key={provider.name}
                  href={provider.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  {provider.name}
                  <ExternalLink className="w-3 h-3" />
                </a>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
